import TagManager from 'react-gtm-module';

import { once } from './clickTracking';

export { isModifiedClick } from './clickTracking';

export enum MegaMenuLevels {
  level_0 = 'level 0',
  level_1 = 'level 1',
  level_2 = 'level 2',
  level_3 = 'level 3',
  level_4 = 'level 4',
}

export interface TrackMegaMenuNavigationProps {
  level: MegaMenuLevels;
  linkUrl: string | null;
  linkText: string | null;
  menuPath: string;
  /**
   * Invoked once the GTM event has been dispatched (or after `eventTimeout`
   * ms as a fallback). Use this to defer navigation so the analytics hit is
   * not lost when the page unloads — critical for cross-origin links on
   * mobile browsers, which routinely drop in-flight beacons on navigation.
   */
  eventCallback?: () => void;
  /** Max time (ms) to wait for the event before running `eventCallback`. */
  eventTimeout?: number;
}

export interface TrackMegaMenuBannerProps {
  linkUrl: string;
  linkText: string;
  menuPath: string;
}

export const MENU_PATH_DELIMITER = '-';

export const trackMegaMenuNavigation = ({
  level,
  linkUrl,
  linkText,
  menuPath,
  eventCallback,
  eventTimeout = 500,
}: TrackMegaMenuNavigationProps) => {
  const onSent = eventCallback ? once(eventCallback) : undefined;

  TagManager.dataLayer({
    dataLayer: {
      event: 'user_interaction',
      event_name: 'click_megamenu_navigation',
      interaction: {
        interaction_location: level,
        ...(linkUrl !== undefined && { link_url: linkUrl }),
        ...(linkText && { link_text: linkText }),
        ...(menuPath && { megamenu_path: menuPath }),
      },
      // GTM invokes `eventCallback` once all tags for this event have fired,
      // or after `eventTimeout`, letting the caller defer navigation.
      ...(onSent && { eventCallback: onSent, eventTimeout }),
    },
  });

  // Fallback: if GTM is blocked or never loaded it will not invoke the
  // callback, so guarantee navigation still happens.
  if (onSent) {
    setTimeout(onSent, eventTimeout + 250);
  }
};

export const trackMegaMenuBanner = ({
  linkUrl,
  linkText,
  menuPath,
}: TrackMegaMenuBannerProps) => {
  TagManager.dataLayer({
    dataLayer: {
      event: 'user_interaction',
      event_name: 'click_megamenu_banner',
      interaction: {
        ...(linkUrl !== undefined && { link_url: linkUrl }),
        ...(linkText && { link_text: linkText }),
        ...(menuPath && { megamenu_path: menuPath }),
      },
    },
  });
};
